import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useShowOutlookMailboxUnavailableModal } from '~/components/OutlookMailboxUnavailableModal/OutlookMailboxUnavailableModal';
import { useStoreActions, useOverlayContext, useStoreStatic } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { EmailAuthErrorResponse } from '~/infra/error-response/EmailAuthErrorResponse';
import { ServerError } from '~/infra/error-response/ErrorResponse';
import { handleRequest } from '~/infra/utils';
import { EmailOnBehalfTypes } from '~/models/EmailOnBehalfSettingsModel';
import { getEmailProviderDetails } from '~/pages/NotificationSettings/NotificationSettings.utils';
import { selectors } from '~/store/state-selectors';
export const emailAuthGenericErrorToastId = (provider)=>"complete-".concat(provider, "-auth-generic-error-toast");
const getProviderSpecificError = (provider, error, emailTexts)=>{
    if (provider === 'gmail') {
        const isMissingRequiredScopes = error.hasServerError(ServerError.InvalidScopeMissingUserInfo) || error.hasServerError(ServerError.InvalidScopeMissingGmailSend);
        if (isMissingRequiredScopes) {
            return {
                errorMessage: emailTexts.permissionsNotGranted(),
                toastId: 'complete-gmail-auth-error-toast'
            };
        }
    } else if (provider === 'outlook') {
        if (error.hasServerError(ServerError.PersonalAccountNotSupported)) {
            return {
                errorMessage: emailTexts.personalOutlookNotSupported(),
                toastId: 'complete-outlook-personal-account-error-toast'
            };
        }
    }
    return {
        errorMessage: emailTexts.failedToConnect({
            provider
        }),
        toastId: emailAuthGenericErrorToastId(provider)
    };
};
export const useEmailAuthCompletion = (param)=>{
    let { businessId, onSuccess, onError, onOutlookMailboxUnavailable, onAccessDenied } = param;
    const location = useLocation();
    const navigate = useNavigate();
    const { api, logBI } = useStoreActions();
    const { startLoader, stopLoader, showErrorToast } = useOverlayContext();
    const { t: emailTexts } = useLocaleKeys('auth.emailAuthCompletion');
    const showOutlookMailboxUnavailableModal = useShowOutlookMailboxUnavailableModal();
    const { getState } = useStoreStatic();
    useEffect(()=>{
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const state = params.get('state');
        const paramError = params.get('error');
        if (!code && !paramError) return;
        const handleEmailAuth = async (provider)=>{
            const scope = params.get('scope');
            const csrfToken = state === null || state === void 0 ? void 0 : state.split(':')[1];
            // both code and businessId are required in gmail and outlook endpoints
            if (!code || !businessId) return;
            startLoader();
            const providerDetails = getEmailProviderDetails(provider);
            let apiCall;
            if (provider === 'gmail') {
                if (!scope) return;
                apiCall = api.notification.emailOnBehalf.completeGmailAuthorization({
                    businessId,
                    code,
                    scope,
                    csrfToken,
                    redirectUri: window.location.origin + window.location.pathname
                });
            } else {
                apiCall = api.notification.emailOnBehalf.completeOutlookAuthorization({
                    businessId,
                    code,
                    csrfToken,
                    redirectUri: window.location.origin + window.location.pathname
                });
            }
            const { success, error } = await handleRequest(apiCall);
            if (success) {
                logBI(providerDetails.biCodes.completeAuth, {
                    vendorId: businessId
                });
                await (onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess());
            } else {
                logBI(providerDetails.biCodes.authError, {
                    vendorId: businessId
                });
                const { OUTLOOK_MAILBOX_UNAVAILABLE_MODAL } = selectors.ff.featureFlagsData(getState());
                const isOutlookMailboxUnavailable = OUTLOOK_MAILBOX_UNAVAILABLE_MODAL ? provider === 'outlook' && error.hasServerError(ServerError.EmailMailboxUnavailable) : false;
                if (isOutlookMailboxUnavailable) {
                    showOutlookMailboxUnavailableModal(new EmailAuthErrorResponse(error).attemptedEmail());
                    onOutlookMailboxUnavailable === null || onOutlookMailboxUnavailable === void 0 ? void 0 : onOutlookMailboxUnavailable();
                } else {
                    const { errorMessage, toastId } = getProviderSpecificError(provider, error, emailTexts);
                    if (onError) {
                        onError(errorMessage, toastId);
                    } else {
                        showErrorToast(errorMessage, toastId);
                    }
                }
            }
            navigate(location.pathname, {
                replace: true
            });
            stopLoader();
        };
        const handleAuth = async ()=>{
            if (paramError === 'access_denied') {
                onAccessDenied === null || onAccessDenied === void 0 ? void 0 : onAccessDenied();
                navigate(location.pathname, {
                    replace: true
                });
                return;
            }
            const stateProvider = state ? state.split(':')[0] : null;
            if (stateProvider === 'outlook') {
                handleEmailAuth(EmailOnBehalfTypes.Outlook);
            } else if (stateProvider === 'gmail') {
                handleEmailAuth(EmailOnBehalfTypes.Gmail);
            }
        };
        handleAuth();
    // WHY: one-shot auth handler keyed on businessId/route only; the callbacks are read at call-time. Adding them re-fires the handler on every parent re-render.
    // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
    }, [
        businessId,
        location.pathname,
        location.search
    ]);
};
